/**
 * 历史记录面板（补齐 AppShell 早先登记的占位面板）。
 *
 * `app/history.ts` 的 `History.entries`/`cursor` 从一开始就为这块面板留了
 * 文档注释（"供历史面板显示"），但从来没有真正接上过——用户看到的
 * "历史记录"面板一直是 AppShell 的静态占位文案"尚无操作"，哪怕内容识别
 * 填充这类异步动作已经真的提交了历史，面板上也完全看不出来，容易让人
 * 以为"点了没反应"。
 *
 * 纯展示组件：条目进、跳转意图出，不认识 Store/History（同 LayersPanel/
 * ActionsPanel 的分层规则）——点哪条历史是应用层的事（多退/多进几步 undo/
 * redo），这里只发一个"我要跳到第几条"的意图。
 */
import type { JSX } from 'solid-js';
import { For } from 'solid-js';
import styles from './HistoryPanel.module.css';

export interface HistoryEntryModel {
  readonly id: number;
  readonly label: string;
}

export interface HistoryPanelProps {
  readonly entries: readonly HistoryEntryModel[];
  /** 当前所在位置：-1 = 打开文档时的基线，否则是 entries 的下标。 */
  readonly cursor: number;
  /** 点某一行 = 跳到那个状态（-1 = 跳回基线）。 */
  readonly onJumpTo: (index: number) => void;
  /**
   * 删除这一条历史及其后所有条目（PS 历史面板垃圾桶：删掉的部分
   * 不能再重做，跟单纯跳过去、还能重做回来的 `onJumpTo` 是两回事）。
   * 基线（"打开文档"）删不掉——那是整份历史的起点，不是一条可删的记录。
   */
  readonly onDelete?: ((index: number) => void) | undefined;
}

export function HistoryPanel(props: HistoryPanelProps): JSX.Element {
  return (
    <ul class={styles.list} data-testid="history-list">
      <li
        class={styles.item}
        classList={{ [styles.current!]: props.cursor === -1 }}
        data-testid="history-entry--1"
        onClick={() => props.onJumpTo(-1)}
      >
        <span class={styles.label}>打开文档</span>
      </li>
      <For each={props.entries}>
        {(entry, index) => (
          <li
            class={styles.item}
            classList={{ [styles.current!]: index() === props.cursor }}
            data-testid={`history-entry-${index()}`}
            onClick={() => props.onJumpTo(index())}
          >
            <span class={styles.label}>{entry.label}</span>
            <button
              type="button"
              class={styles.delete}
              aria-label="删除这一步及之后的记录"
              title="删除这一步及之后的记录"
              data-testid={`history-delete-${index()}`}
              onClick={(event) => {
                event.stopPropagation();
                props.onDelete?.(index());
              }}
            >
              ×
            </button>
          </li>
        )}
      </For>
    </ul>
  );
}
